import { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { Trash2, Plus } from "lucide-react";
import mainbannerimage from "../../assets/Mainbanner/main.png";
import logo2 from "../../assets/logo/2.png";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../components/usercontext/user_context";

// Кастомный MonthPicker
const MonthPicker = ({ value, onChange, placeholder }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [month, setMonth] = useState(value ? value.split("-")[1] : "");
  const [year, setYear] = useState(value ? value.split("-")[0] : "");

  const handleSelect = () => {
    if (month && year) {
      onChange(`${year}-${month.padStart(2, "0")}`);
      setShow(false);
    }
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        readOnly
        className="w-full py-2 px-4 border rounded-md outline-none focus:border-[#006674] cursor-pointer"
        placeholder={placeholder}
        value={value}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full">
          <input
            type="number"
            placeholder={t("year")}
            min="1900"
            max="2100"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="w-full mb-1 p-1 border rounded"
          />
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-full p-1 border rounded mb-1"
          >
            <option value="">{t("month")}</option>
            {[...Array(12).keys()].map((m) => (
              <option key={m + 1} value={String(m + 1).padStart(2, "0")}>
                {m + 1}
              </option>
            ))}
          </select>
          <button
            className="w-full bg-[#006674] text-white py-1 rounded"
            onClick={handleSelect}
          >
            {t("coose")}
          </button>
        </div>
      )}
    </div>
  );
};

// Кастомный GenderPicker
const GenderPicker = ({ value, onChange, placeholder }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const options = [t("male"), t("female"), t("other")];

  const handleSelect = (val) => {
    onChange(val);
    setShow(false);
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        readOnly
        placeholder={placeholder}
        className="w-full py-2 px-4 border rounded-md outline-none focus:border-[#006674] cursor-pointer"
        value={value}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full">
          {options.map((opt) => (
            <div
              key={opt}
              className="p-2 hover:bg-[#006674] transition-all duration-200 hover:text-white cursor-pointer rounded"
              onClick={() => handleSelect(opt)}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CVForm = () => {
  const { t } = useTranslation();
  const allLanguagesObj = t("languageslist", { returnObjects: true });
  const levelsObj = t("levels", { returnObjects: true });
  const levels2Obj = t("levels2", { returnObjects: true });
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, updateUser } = useContext(UserContext);
  
  // 🔹 Получаем данные для редактирования
  const editData = location.state?.formData || null;
  const isEditing = location.state?.isEditing || false;
  const cvId = location.state?.cvId || id;
  
  const allLanguages = Object.entries(allLanguagesObj);
  const levels = Object.entries(levelsObj);
  const levels2 = Object.entries(levels2Obj);
  
  const [formData, setFormData] = useState({
    photo: null,
    photoPreview: null,
    name: "",
    lastname: "",
    age: "",
    gender: "",
    phone: "",
    email: "",
    about: "",
    location: "",
    experiences: [
      { company: "", position: "", start: "", end: "", description: "" },
    ],
    education: [
      { school: "", degree: "", Specialization: "", start: "", end: "" },
    ],
    awards: [{ field: "", qualification: "", title: "", year: "" }],
    volunteering: [{ field: "", role: "" }],
    languages: [{ name: "", level: "", level2: "" }],
    skills: [""],
    computerSkills: [""],
    additionalInfo: "",
    headings: {
      name: t("Name"),
      lastname: t("Lastname"),
      age: t("Age"),
      gender: t("Gender"),
      phone: t("Phone"),
      email: t("Email"),
      about: t("About"),
      workExperience: t("workexperiance"),
      education: t("Education"),
      awards: t("Awards"),
      volunteering: t("Volunteer"),
      languages: t("languages"),
      skills: t("skills"),
      additionalInfo: t("additionalInfo"),
      location: t("location2"),
      computerSkillstitle: t("computerskills"),
      language: t("language"),
      languageselect1: t("languageselect1"),
      languageselect2: t("languageselect2"),
      start: t("start"),
      end: t("end"),
      present: t("Present"),
    },
  });

  // 🔹 Загружаем данные для редактирования
  useEffect(() => {
    if (isEditing && editData) {
      console.log("📝 Загрузка данных для редактирования:", editData);
      setFormData(prev => ({
        ...prev,
        name: editData.name || "",
        lastname: editData.lastname || "",
        age: editData.age || "",
        gender: editData.gender || "",
        phone: editData.phone || "",
        email: editData.email || "",
        about: editData.about || "",
        location: editData.location || "",
        additionalInfo: editData.additionalInfo || "",
        photo: null,
        photoFile: null,
        photoPreview: editData.photo ? `/api/${editData.photo}` : null,
        experiences: editData.experiences?.length > 0 ? editData.experiences : prev.experiences,
        education: editData.education?.length > 0 ? editData.education : prev.education,
        awards: editData.awards?.length > 0 ? editData.awards : prev.awards,
        volunteering: editData.volunteering?.length > 0 ? editData.volunteering : prev.volunteering,
        languages: editData.languages?.length > 0 ? editData.languages : prev.languages,
        skills: editData.skills?.length > 0 ? editData.skills : prev.skills,
        computerSkills: editData.computerSkills?.length > 0 ? editData.computerSkills : prev.computerSkills,
        headings: editData.headings || prev.headings,
      }));
    }
  }, [isEditing, editData]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        photo: file,
        photoFile: file,
        photoPreview: URL.createObjectURL(file),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const userId = localStorage.getItem("userId");

    const dataToSend = {
      ...formData,
      user_id: userId || 0,
      id: isEditing ? cvId : undefined,
      headings: {
        name: t("Name"),
        lastname: t("Lastname"),
        age: t("Age"),
        gender: t("Gender"),
        phone: t("Phone"),
        email: t("Email"),
        about: t("About"),
        location: t("location2"),
        workExperience: t("workexperiance"),
        education: t("Education"),
        awards: t("Awards"),
        volunteering: t("Volunteer"),
        languages: t("languages"),
        skills: t("skills"),
        computerSkills: t("computerskills"),
        additionalInfo: t("additionalInfo"),
        language: t("language"),
        languageselect1: t("languageselect1"),
        languageselect2: t("languageselect2"),
        start: t("start"),
        end: t("end"),
        present: t("Present"),
      },
    };

    const formDataToSend = new FormData();
    formDataToSend.append("data", JSON.stringify(dataToSend));

    if (formData.photoFile) {
      formDataToSend.append("photo", formData.photoFile);
    }

    try {
      const url = isEditing ? "/api/update_cv.php" : "/api/save_cv.php";
      const resp = await fetch(url, {
        method: "POST",
        credentials: "include",
        body: formDataToSend,
      });

      const result = await resp.json();
      console.log("Ответ сервера:", result);

      if (result.success) {
        updateUser({ cv_id: result.cv_id || cvId });
        navigate(`/cv/${result.cv_id || cvId}`);
      } else {
        alert("Ошибка: " + (result.message || "Не удалось сохранить CV"));
      }
    } catch (err) {
      console.error("Ошибка сети", err);
      alert("Ошибка сети");
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setFormData((prev) => {
      const updated = [...prev[field]];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [field]: updated };
    });
  };

  const addField = (field, newItem) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], newItem],
    }));
  };

  const removeField = (field, index) => {
    setFormData((prev) => {
      const updated = [...prev[field]];
      updated.splice(index, 1);
      return { ...prev, [field]: updated };
    });
  };

  const [showLang, setShowLang] = useState(null);
  const [showLevel, setShowLevel] = useState(null);
  const [showLevel2, setShowLevel2] = useState(null);

  return (
    <section
      className="p-5 max-sm:py-5 max-sm:px-2"
      style={{
        backgroundImage: `url(${mainbannerimage})`,
        animationDelay: ".2s",
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="max-w-3xl flex rounded-[24px] justify-center items-center flex-col opacity-0 animate-[showtitle_1s_forwards] gap-3 mx-auto p-6 max-sm:p-3 space-y-4 bg-white"
      >
        <h3 className="font-bold tracking-widest bg-[#006674] py-3 rounded-t-xl text-white w-full text-center uppercase text-3xl max-sm:text-xl">
          {isEditing ? (t("etitcvtitle") || "Редактировать CV") : t("createcvtitle")}
        </h3>

        {/* переключатели языка */}
        <ul className="justify-center flex items-center gap-1">
          <li>
            <button
              type="button"
              onClick={() => t.changeLanguage("am")}
              className={`w-[150px] max-sm:w-auto max-sm:text-[14px] py-2 px-3 rounded-md cursor-pointer shadow-xl shadow-slate-400 transition-all duration-200 
              ${t.language === "am" ? "bg-[#006674] text-white" : "bg-gray-200 hover:bg-[#006674] hover:text-white"}`}
            >
              Հայերեն
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => t.changeLanguage("ru")}
              className={`w-[150px] max-sm:w-auto max-sm:text-[14px] py-2 px-3 rounded-md cursor-pointer shadow-xl shadow-slate-400 transition-all duration-200 
              ${t.language === "ru" ? "bg-[#006674] text-white" : "bg-gray-200 hover:bg-[#006674] hover:text-white"}`}
            >
              На Русском
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => t.changeLanguage("en")}
              className={`w-[150px] max-sm:w-auto max-sm:text-[14px] py-2 px-3 rounded-md cursor-pointer shadow-xl shadow-slate-400 transition-all duration-200 
              ${t.language === "en" ? "bg-[#006674] text-white" : "bg-gray-200 hover:bg-[#006674] hover:text-white"}`}
            >
              In English
            </button>
          </li>
        </ul>

        {/* фото */}
        <div className="w-full flex justify-evenly items-start">
          <div
            className="min-w-[200px] relative h-[200px] max-lg:w-[300px] max-lg:h-[300px] max-sm:h-[200px] max-sm:w-[200px] 
             bg-[#006674] text-white flex justify-center items-center 
             shadow-[inset_0_0_20px_black,0_20px_20px] text-[18px] 
             cursor-pointer rounded-xl border-[5px] border-white shadow-gray-500"
          >
            {formData.photoPreview ? (
              <img
                src={formData.photoPreview}
                alt="Uploaded"
                className="w-full h-full object-cover rounded-xl"
              />
            ) : (
              <span>{t("Uploadphoto") || "Upload Photo"}</span>
            )}

            <label
              htmlFor="editfile"
              className="w-[40px] h-[40px] flex absolute -right-5 top-[90%] justify-center items-center rounded-full bg-[#006674] text-white cursor-pointer"
            >
              <FontAwesomeIcon icon={faEdit} />
            </label>
            <input
              type="file"
              id="editfile"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Личные данные */}
        <div className="grid w-full grid-cols-1 sm:grid-cols-1 gap-4">
          <div className="w-full flex justify-between items-center">
            <div className="flex w-full flex-col gap-3">
              <input
                className="w-[350px] max-sm:w-full py-2 px-4 text-xl outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                placeholder={t("name")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              <input
                className="w-[350px] max-sm:w-full py-2 px-4 text-xl outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                placeholder={t("lastname")}
                value={formData.lastname}
                onChange={(e) =>
                  setFormData({ ...formData, lastname: e.target.value })
                }
              />
            </div>
            <img
              className="max-w-[250px] max-h-[150px] mr-4 max-sm:min-w-[80px] max-sm:min-h-[80px] object-cover"
              src={logo2}
              alt=""
            />
          </div>
          <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-4">
            <input
              className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
              placeholder={t("age")}
              value={formData.age}
              onChange={(e) =>
                setFormData({ ...formData, age: e.target.value })
              }
            />
            <GenderPicker
              value={formData.gender}
              placeholder={t("gender")}
              onChange={(val) => setFormData({ ...formData, gender: val })}
            />
            <input
              className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
              placeholder={t("cvphone")}
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
            />
            <input
              className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
              placeholder={t("cvemail")}
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
          </div>
          <textarea
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
            placeholder={t("location")}
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
          />

          {/* Обо мне */}
          <textarea
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
            placeholder={t("aboutme")}
            value={formData.about}
            onChange={(e) =>
              setFormData({ ...formData, about: e.target.value })
            }
          />

          {/* Опыт работы */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">
              {t("workexperiance")}
            </h2>
            {formData.experiences.map((exp, i) => (
              <div
                key={i}
                className="card mt-2 gap-3 flex flex-col border p-4 rounded-xl shadow"
              >
                <input
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                  placeholder={t("companytitle")}
                  value={exp.company}
                  onChange={(e) =>
                    handleArrayChange(
                      "experiences",
                      i,
                      "company",
                      e.target.value
                    )
                  }
                />
                <input
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                  placeholder={t("Position")}
                  value={exp.position}
                  onChange={(e) =>
                    handleArrayChange(
                      "experiences",
                      i,
                      "position",
                      e.target.value
                    )
                  }
                />
                <div className="grid grid-cols-2 gap-2">
                  <MonthPicker
                    value={exp.start}
                    placeholder={t("start")}
                    onChange={(val) =>
                      handleArrayChange("experiences", i, "start", val)
                    }
                  />
                  <MonthPicker
                    value={exp.end}
                    placeholder={t("end")}
                    onChange={(val) =>
                      handleArrayChange("experiences", i, "end", val)
                    }
                  />
                </div>
                <textarea
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                  placeholder={t("more")}
                  value={exp.description}
                  onChange={(e) =>
                    handleArrayChange(
                      "experiences",
                      i,
                      "description",
                      e.target.value
                    )
                  }
                />
                <div className="flex gap-3 mt-2">
                  <button
                    type="button"
                    className="px-3 py-2 flex justify-center items-center bg-[#006674] text-white rounded-full"
                    onClick={() =>
                      addField("experiences", {
                        company: "",
                        position: "",
                        start: "",
                        end: "",
                        description: "",
                      })
                    }
                  >
                    <Plus size={18} /> {t("add")}
                  </button>

                  <button
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                    type="button"
                    onClick={() => removeField("experiences", i)}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Образование */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">{t("Education")}</h2>
            {formData.education.map((edu, i) => (
              <div
                key={i}
                className="card flex flex-col mt-2 gap-3 border p-4 rounded-xl shadow"
              >
                <input
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                  placeholder={t("InstitutionName")}
                  value={edu.school}
                  onChange={(e) =>
                    handleArrayChange("education", i, "school", e.target.value)
                  }
                />
                <input
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                  placeholder={t("Specialization")}
                  value={edu.Specialization}
                  onChange={(e) =>
                    handleArrayChange(
                      "education",
                      i,
                      "Specialization",
                      e.target.value
                    )
                  }
                />
                <div className="grid grid-cols-2 gap-2">
                  <MonthPicker
                    value={edu.start}
                    placeholder={t("start")}
                    onChange={(val) =>
                      handleArrayChange("education", i, "start", val)
                    }
                  />
                  <MonthPicker
                    value={edu.end}
                    placeholder={t("end")}
                    onChange={(val) =>
                      handleArrayChange("education", i, "end", val)
                    }
                  />
                </div>
                <input
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                  placeholder={t("degree")}
                  value={edu.degree}
                  onChange={(e) =>
                    handleArrayChange("education", i, "degree", e.target.value)
                  }
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="flex items-center gap-2 bg-[#006674] text-white px-3 py-1 rounded-full"
                    onClick={() =>
                      addField("education", {
                        school: "",
                        degree: "",
                        Specialization: "",
                        start: "",
                        end: "",
                      })
                    }
                  >
                    <Plus size={18} /> {t("add")}
                  </button>
                  <button
                    type="button"
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                    onClick={() => removeField("education", i)}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Награды и достижения */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">{t("Awards")}</h2>
            {formData.awards.map((award, i) => (
              <div
                key={i}
                className="card flex flex-col gap-3 border p-4 rounded-xl shadow"
              >
                <input
                  placeholder={t("Field")}
                  value={award.field}
                  onChange={(e) =>
                    handleArrayChange("awards", i, "field", e.target.value)
                  }
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                <input
                  placeholder={t("Qualification")}
                  value={award.qualification}
                  onChange={(e) =>
                    handleArrayChange(
                      "awards",
                      i,
                      "qualification",
                      e.target.value
                    )
                  }
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                <input
                  placeholder={t("title")}
                  value={award.title}
                  onChange={(e) =>
                    handleArrayChange("awards", i, "title", e.target.value)
                  }
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                <input
                  type="number"
                  placeholder={t("year")}
                  value={award.year}
                  onChange={(e) =>
                    handleArrayChange("awards", i, "year", e.target.value)
                  }
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      addField("awards", {
                        field: "",
                        qualification: "",
                        title: "",
                        year: "",
                      })
                    }
                    className="flex items-center gap-2 bg-[#006674] text-white px-3 py-1 rounded-full"
                  >
                    <Plus size={18} /> {t("add")}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeField("awards", i)}
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Волонтерский опыт */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">{t("Volunteer")}</h2>
            {formData.volunteering.map((vol, i) => (
              <div
                key={i}
                className="card flex flex-col gap-3 border p-4 rounded-xl shadow"
              >
                <input
                  placeholder={t("Field")}
                  value={vol.field}
                  onChange={(e) =>
                    handleArrayChange(
                      "volunteering",
                      i,
                      "field",
                      e.target.value
                    )
                  }
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                <input
                  placeholder={t("role")}
                  value={vol.role}
                  onChange={(e) =>
                    handleArrayChange("volunteering", i, "role", e.target.value)
                  }
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      addField("volunteering", { field: "", role: "" })
                    }
                    className="flex items-center gap-2 bg-[#006674] text-white px-3 py-1 rounded-full"
                  >
                    <Plus size={18} /> {t("add")}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeField("volunteering", i)}
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Владение языками */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">{t("languages")}</h2>
            {formData.languages.map((lang, i) => (
              <div
                key={i}
                className="flex max-sm:flex-col max-sm:gap-0 gap-3 mb-2 items-center"
              >
                <div className="flex gap-3 w-full">
                  {/* Language select */}
                  <div className="relative w-full">
                    <input
                      type="text"
                      readOnly
                      placeholder={t("language")}
                      className="w-full py-2 px-4 mt-6 border rounded-md outline-none focus:border-[#006674] cursor-pointer"
                      value={
                        lang.name
                          ? t(`languageslist.${lang.name}`, lang.name)
                          : ""
                      }
                      onClick={() => setShowLang(showLang === i ? null : i)}
                    />
                    {showLang === i && (
                      <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full max-h-60 overflow-y-auto">
                        {allLanguages.map(([key, label]) => (
                          <div
                            key={key}
                            className="p-2 hover:bg-[#006674] transition-all duration-200 hover:text-white cursor-pointer rounded"
                            onClick={() => {
                              handleArrayChange(
                                "languages",
                                i,
                                "name",
                                key
                              );
                              setShowLang(null);
                            }}
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Кнопки для мобильных */}
                  {i >= 1 && (
                    <button
                      type="button"
                      onClick={() => removeField("languages", i)}
                      className="min-w-[40px] hidden max-sm:flex mt-6 h-[40px] justify-center items-center bg-red-600 text-white rounded-full"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                  {i === formData.languages.length - 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        addField("languages", {
                          name: "",
                          level: "",
                          level2: "",
                        });
                      }}
                      className="min-w-[40px] hidden max-sm:flex h-[40px] mt-6 justify-center items-center bg-[#006674] text-white rounded-full"
                    >
                      <Plus size={18} />
                    </button>
                  )}
                </div>

                <div className="flex w-full gap-3 max-sm:flex-col">
                  {/* Level select */}
                  <div className="relative w-full">
                    <label className="text-sm ml-4 text-[#006674]">
                      {t("languageselect1")}
                    </label>
                    <input
                      type="text"
                      readOnly
                      placeholder={t("languageselect1")}
                      className="w-full py-2 px-4 border rounded-md outline-none focus:border-[#006674] cursor-pointer"
                      value={lang.level ? t(`levels.${lang.level}`, lang.level) : ""}
                      onClick={() => setShowLevel(showLevel === i ? null : i)}
                    />
                    {showLevel === i && (
                      <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full">
                        {levels.map(([key, label]) => (
                          <div
                            key={key}
                            className="p-2 hover:bg-[#006674] transition-all duration-200 hover:text-white cursor-pointer rounded"
                            onClick={() => {
                              handleArrayChange(
                                "languages",
                                i,
                                "level",
                                key
                              );
                              setShowLevel(null);
                            }}
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="relative w-full">
                    <label className="text-sm ml-4 text-[#006674]">
                      {t("languageselect2")}
                    </label>
                    <input
                      type="text"
                      readOnly
                      placeholder={t("languageselect2")}
                      className="w-full py-2 px-4 border rounded-md outline-none focus:border-[#006674] cursor-pointer"
                      value={lang.level2 ? t(`levels2.${lang.level2}`, lang.level2) : ""}
                      onClick={() => setShowLevel2(showLevel2 === i ? null : i)}
                    />
                    {showLevel2 === i && (
                      <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full">
                        {levels2.map(([key, label]) => (
                          <div
                            key={key}
                            className="p-2 hover:bg-[#006674] transition-all duration-200 hover:text-white cursor-pointer rounded"
                            onClick={() => {
                              handleArrayChange(
                                "languages",
                                i,
                                "level2",
                                key
                              );
                              setShowLevel2(null);
                            }}
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Кнопки для десктопа */}
                {i >= 1 && (
                  <button
                    type="button"
                    onClick={() => removeField("languages", i)}
                    className="min-w-[40px] max-sm:hidden mt-6 h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
                {i === formData.languages.length - 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      addField("languages", {
                        name: "",
                        level: "",
                        level2: "",
                      });
                    }}
                    className="min-w-[40px] max-sm:hidden h-[40px] mt-6 flex justify-center items-center bg-[#006674] text-white rounded-full"
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>
            ))}
          </section>

          {/* Хард/софт скиллы */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">{t("skills")}</h2>
            {formData.skills.map((skill, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  placeholder={t("skill")}
                  value={skill}
                  onChange={(e) => {
                    const updated = [...formData.skills];
                    updated[i] = e.target.value;
                    setFormData({ ...formData, skills: updated });
                  }}
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                {i >= 1 && (
                  <button
                    type="button"
                    onClick={() => removeField("skills", i)}
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
                {i < 1 && (
                  <button
                    type="button"
                    onClick={() => addField("skills", "")}
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-[#006674] text-white rounded-full"
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>
            ))}
          </section>

          {/* Компьютерные навыки */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">
              {t("computerskills")}
            </h2>
            {formData.computerSkills.map((skill, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  placeholder={t("computerskill")}
                  value={skill}
                  onChange={(e) => {
                    const updated = [...formData.computerSkills];
                    updated[i] = e.target.value;
                    setFormData({ ...formData, computerSkills: updated });
                  }}
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 transition-all duration-200 focus:border-[#006674] focus:shadow-xl"
                />
                {i >= 1 && (
                  <button
                    type="button"
                    onClick={() => removeField("computerSkills", i)}
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
                {i < 1 && (
                  <button
                    type="button"
                    onClick={() => addField("computerSkills", "")}
                    className="min-w-[40px] h-[40px] flex justify-center items-center bg-[#006674] text-white rounded-full"
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>
            ))}
          </section>

          {/* Доп. информация */}
          <section className="w-full">
            <h2 className="text-lg text-[#006674] mb-2">
              {t("additionalInfo")}
            </h2>
            <textarea
              placeholder={t("Info")}
              value={formData.additionalInfo}
              onChange={(e) =>
                setFormData({ ...formData, additionalInfo: e.target.value })
              }
              className="w-full py-2 px-4 border rounded-md outline-none"
            />
          </section>
        </div>

        {/* кнопка */}
        <button
          type="submit"
          className="w-full bg-[#006674] text-white py-2 rounded-xl hover:bg-[#005060] transition"
        >
          {isEditing ? (t("saveChanges") || "Сохранить изменения") : t("save")}
        </button>
      </form>
    </section>
  );
};

export default CVForm;